import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {Website} from "../../../website";
import {Product} from "../../../product";
import * as fromStore from "../../../../../store";
import {ActivityAction, ActivityStatus} from "../../../../../activities/activity";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../../../../../store";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {combineLatest, Subject} from "rxjs";
import {Config} from "../../../../../config/config";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {EcommerceService} from "../../ecommerce.service";
import {
  NotificationModalComponent
} from "../../../../../shared/components/dialogs/notification-modal/notification-modal.component";
import {DbModalService} from "../../../../../modules/modal/modal";


@Component({
    selector: 'db-ecommerce-tax-main',
    templateUrl: './ecommerce-tax-main.component.html',
    styleUrls: ['./ecommerce-tax-main.component.scss'],
    standalone: false
})
export class EcommerceTaxMainComponent implements OnInit, OnDestroy {

  @Input() website: Website;
  @Input() taxPlugin: Product;
  @Input() connectTaxesViaAdmin: boolean;
  public connectTaxesEnabled: boolean;
  public loading: boolean;
  public enableBtnLoading: boolean;
  public hasConnectTaxesOption: boolean;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<DbStoreState>,
    private _modalService: DbModalService,
    private _ecommerceService: EcommerceService
  ) {
  }

  ngOnInit() {
    this._getGeneralSettings();
    this._ongoingActivities();
    this._getTaxPluginData();
  }

  public installAndActivateTax(enable: string): void {
    if (!this.taxPlugin || !this.taxPlugin?.installed || !this.taxPlugin?.active) {
      this.enableBtnLoading = true;
      const network = this.website.domain_hash ? 1 : 0;
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: this.website,
        products: [this.taxPlugin],
        action: ActivityAction.INSTALL_ACTIVATE,
        network: network
      }));
    }
    this.connectTaxes(enable);
  }

  private _getGeneralSettings(): void {
    this._store.select(fromStore.getEcommerceGeneralSettingsLoaded(this.website?.id))
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => combineLatest(
                this._store.select(fromStore.getEcommerceConnectTaxesEnabled(this.website?.id))
            )),
            filter(([connectTaxesEnabled]) => !!connectTaxesEnabled ),
            takeUntil(this._destroyStream$)
        )
        .subscribe(([connectTaxesEnabled]) => {
          this.loading = false;
          this.hasConnectTaxesOption = !!connectTaxesEnabled?.value;
          this.connectTaxesEnabled = connectTaxesEnabled?.value === 'yes';
        });
  }


  public showSwitchingDialog(automation: boolean): void {
    const headerText = !!automation ? 'Switching to automated tax rates' : 'Switching to manual tax rates';
    const descText = !!automation ? 'Are you sure you want to switch to automated tax rates?' : 'Are you sure you want to switch to manual tax rates?';
    const confirmationModal = this._modalService.open(NotificationModalComponent, {width: 500});
    confirmationModal.componentInstance.header = headerText;
    confirmationModal.componentInstance.message = descText;
    confirmationModal.componentInstance.buttonTxt = 'Proceed';
    confirmationModal.componentInstance.secondaryBtnText = 'Cancel';
    confirmationModal.componentInstance.buttonAdditionalClass = 'ds-btn-black';
    confirmationModal.closed.subscribe((value) => {
      value && this.connectTaxes(!!automation ? 'yes' : 'no');
    });
  }

  private _getTaxPluginData(): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(this.website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteEcommerceTaxPlugin(this.website?.id))
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([taxPluginProduct]) => {
        if (taxPluginProduct) {
          this.taxPlugin = taxPluginProduct;
        }
      });
  }

  public connectTaxes(enabled: string): void {
    this.enableBtnLoading = true;
    const taxSettingsForm = {
      connectTaxes: enabled
    };
    this._ecommerceService.updateTaxSettings(this.website?.id, taxSettingsForm)
        .pipe(filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response: DbResponse): void => {
          if (response.success && response?.data) {
            this._store.dispatch(new fromStore.LoadEcommerceTaxRates({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceGeneralSettings({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceTaxSettings({websiteId: this.website?.id}));
          }
          else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
        });
  }

  private _ongoingActivities(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity => activity?.websiteId === this.website?.id && activity?.action === ActivityAction.INSTALL_ACTIVATE && activity?.meta?.product_data?.slug === Config.woocommerceTaxPluginSlug)
      )
      .subscribe(activity => {
        if (activity.status) {
          if (activity.status === ActivityStatus.DONE) {
            this.loading = false;
            this.enableBtnLoading = false;
            this._store.dispatch(new fromStore.LoadWebsiteProducts(this.website?.id));
          }
          else if (activity.status === ActivityStatus.FAILED) {
            this.loading = false;
            this.enableBtnLoading = false;
            NotifierComponent.notify(new Notification('Something went wrong...', NotificationType.DANGER));
          }
        }
      });
  }

  ngOnDestroy() {

  }
}
